import { toShippedSharedIcon } from "./sharedIcons";
const DEFAULT_CDN_URL = "https://cdn-dev.culturemap.app";

const normaliseBaseUrl = (value?: string | null): string => {
  if (!value) {
    return DEFAULT_CDN_URL;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return DEFAULT_CDN_URL;
  }

  const withoutTrailingSlash = trimmed.replace(/\s+/g, "").replace(/\/+$/, "");

  try {
    const url = new URL(withoutTrailingSlash);
    return url.origin;
  } catch {
    try {
      const coerced = new URL(`https://${withoutTrailingSlash.replace(/^https?:\/\//, "")}`);
      return coerced.origin;
    } catch {
      return DEFAULT_CDN_URL;
    }
  }
};

// NB: reference `process.env.REACT_APP_CLOUDFRONT_URL` directly — webpack/DefinePlugin
// inlines it as a string literal at build time. A `typeof process` guard here would
// always be false in the browser (process is not defined) and silently fall back to cdn-dev.
const CDN_BASE_URL = normaliseBaseUrl(process.env.REACT_APP_CLOUDFRONT_URL);

// The build-time host is only a starting point. The backend's `mediaHost`
// (GET /api/v2/config/) is the one setting that decides which host media is
// served from, and every URL the API returns is on it; the app switches to it
// as soon as the config loads so icon URLs, credentialed fetches and the
// "is this our CDN?" checks agree with the backend.
let runtimeCdnBaseUrl: string | null = null;

// With neither the backend's mediaHost nor a build-time host, media falls back to
// cdn-dev, which on staging or production is another environment's CDN. That is a
// deploy mistake, so it is said out loud, once per page load.
const HAS_BUILD_TIME_HOST = Boolean(process.env.REACT_APP_CLOUDFRONT_URL?.trim());
let warnedNoMediaHost = false;

export const setEnvCdnBaseUrl = (host?: string | null): void => {
  const trimmed = host?.trim();
  runtimeCdnBaseUrl = trimmed ? normaliseBaseUrl(trimmed) : null;
  if (!trimmed && !HAS_BUILD_TIME_HOST && !warnedNoMediaHost) {
    warnedNoMediaHost = true;
    console.warn(
      `[cdn] No media host is set: the backend sends no mediaHost (its CLOUDFRONT_DOMAIN) and the build has no ` +
        `REACT_APP_CLOUDFRONT_URL, so media is loaded from ${DEFAULT_CDN_URL}. Set one of them for this environment.`
    );
  }
};

export const getEnvCdnBaseUrl = (): string => runtimeCdnBaseUrl ?? CDN_BASE_URL;

export const getEnvCdnHostname = (): string => {
  try {
    return new URL(getEnvCdnBaseUrl()).hostname;
  } catch {
    return "cdn-dev.culturemap.app";
  }
};

export const isEnvCdnUrl = (value?: string | null): boolean => {
  if (!value) {
    return false;
  }

  const hostname = getEnvCdnHostname();

  try {
    return new URL(value).hostname === hostname;
  } catch {
    return value.includes(hostname);
  }
};

export const buildEnvCdnUrl = (path: string): string => {
  const normalisedPath = path.replace(/^\/*/, "");
  return `${getEnvCdnBaseUrl()}/${normalisedPath}`;
};

/**
 * Resolves a config/icon reference to a loadable URL. Single source of truth for
 * both `<img>` consumers and the map's `styleimagemissing` fetch path:
 *  - absolute `http(s):`/`data:`/`blob:` URLs pass through unchanged
 *  - the shared library (`icons/imw/…`, or its older `public-icons/imw/…` key for
 *    an icon the app ships) resolves against `PUBLIC_URL`, like other app-bundled
 *    assets (`icons/…`)
 *  - org uploads and other CDN assets (`public-icons/…`) resolve to a signed
 *    CloudFront URL
 */
export const resolveIconUrl = (ref?: string | null): string => {
  if (!ref) {
    return "";
  }
  if (/^(https?:\/\/|data:|blob:)/i.test(ref)) {
    return ref;
  }
  const normalisedPath = toShippedSharedIcon(ref).replace(/^\/+/, "");
  if (/^public-icons\//i.test(normalisedPath)) {
    return buildEnvCdnUrl(normalisedPath);
  }
  const base = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  return `${base}/${normalisedPath}`;
};
